import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useUrlFilters } from '../hooks/useUrlFilters';
import { applyFilters } from '../lib/filters/applyFilters';
import { FilterToolbar } from '../components/FilterToolbar';
import { GroupingToggle } from '../components/GroupingToggle';
import { useItems } from '../hooks/useItems';
import { KanbanBoard } from '../components/KanbanBoard';
import { ItemDrawer } from '../components/ItemDrawer';
import type { GroupBy } from '../lib/filters/types';

export function PortfolioPage() {
  const { user, roles } = useAuth();
  const [params] = useSearchParams();
  const { filters, setFilters } = useUrlFilters();
  const { data: itemsAll, isLoading, isError } = useItems(filters.showArchived);

  const newHref = (() => { const p = new URLSearchParams(params); p.set('item', 'new'); return `?${p.toString()}`; })();

  const userId = user?.userId ?? '_anon';
  const isScorerOnly = roles.includes('scorer') && !roles.includes('leader') && !roles.includes('admin');
  const roleDefault: GroupBy = isScorerOnly ? 'lifecycle' : 'decision';
  const stored = (typeof localStorage !== 'undefined' ? localStorage.getItem(`kanbanGroupBy:${userId}`) : null) as GroupBy | null;
  const group: GroupBy = filters.group ?? stored ?? roleDefault;

  const setGroup = (g: GroupBy) => {
    try { localStorage.setItem(`kanbanGroupBy:${userId}`, g); } catch { /* ignore */ }
    setFilters({ ...filters, group: g });
  };

  const filtered = useMemo(() => applyFilters(itemsAll ?? [], filters), [itemsAll, filters]);

  if (isError) return <p className="text-red-700">Failed to load items.</p>;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">Portfolio</h1>
        <span className="text-sm text-slate-500">{filtered.length} {filtered.length === 1 ? 'item' : 'items'}</span>
      </div>
      <FilterToolbar filters={filters} onChange={setFilters} />
      <div className="flex items-center"><GroupingToggle value={group} onChange={setGroup} /></div>
      {isLoading ? <p className="text-slate-500">Loading portfolio…</p> : <KanbanBoard items={filtered} groupBy={group} />}
      <Link to={{ search: newHref }} className="fixed bottom-6 right-6 rounded-full bg-brand px-4 py-2 text-sm text-white shadow-lg transition-colors hover:bg-brand-dark">
        + New item
      </Link>
      <ItemDrawer />
    </div>
  );
}
